import { useEffect, useMemo, useRef, useState } from 'react';

const emptyFields = {
  title: '',
  change_number: '',
  scope_changes: '',
  schedule_changes: '',
  price_adjustment: '',
  payment_terms: '',
};

function errorMessage(error, fallback) {
  return error?.message || fallback;
}

export default function useNativeChangeOrderEditor({
  jobId,
  token,
  items,
  editOrder,
  onEditConsumed,
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [context, setContext] = useState(null);
  const [contextError, setContextError] = useState('');
  const [fields, setFields] = useState(emptyFields);
  const [files, setFiles] = useState([]);
  const [revisionOf, setRevisionOf] = useState('');
  const [expectedHash, setExpectedHash] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewReady, setPreviewReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState('');
  const [contextAttempt, setContextAttempt] = useState(0);
  const previewUrlRef = useRef('');
  const requestVersion = useRef(0);
  const onEditConsumedRef = useRef(onEditConsumed);
  onEditConsumedRef.current = onEditConsumed;

  const authHeaders = useMemo(() => (token ? { 'x-auth-token': token } : {}), [token]);

  useEffect(
    () => () => {
      requestVersion.current += 1;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!editOrder) return;
    requestVersion.current += 1;
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = '';
    setPreviewUrl('');
    const data = editOrder.editor_data || {};
    setFields({
      title: data.title || editOrder.title || '',
      change_number: data.change_number || editOrder.change_number || '',
      scope_changes: data.scope_changes || '',
      schedule_changes: data.schedule_changes || '',
      price_adjustment: data.price_adjustment ?? '',
      payment_terms: data.payment_terms || '',
    });
    setRevisionOf(String(editOrder.id));
    setExpectedHash(editOrder.document_hash || '');
    setContext(editOrder.editor_data?.context || null);
    setContextError('');
    setFiles([]);
    setError('');
    setPreviewReady(false);
    setOpen(true);
    onEditConsumedRef.current?.();
  }, [editOrder]);

  useEffect(() => {
    if (!open || !jobId || context || editOrder?.editor_data?.context) return undefined;
    let active = true;
    setContextError('');
    fetch(`/api/jobs/${encodeURIComponent(jobId)}/change-orders/context`, {
      headers: authHeaders,
    })
      .then(async (response) => {
        if (!response.headers.get('content-type')?.includes('application/json')) {
          throw new Error(
            'The change-order service is unavailable. Refresh after updating the app.',
          );
        }
        const payload = await response.json().catch(() => ({}));
        if (!response.ok)
          throw new Error(payload.error || 'Unable to load job and contract context.');
        return payload.context || {};
      })
      .then((value) => {
        if (active) setContext(value);
      })
      .catch((fetchError) => {
        if (active)
          setContextError(errorMessage(fetchError, 'Unable to load job and contract context.'));
      });
    return () => {
      active = false;
    };
  }, [open, jobId, authHeaders, context, contextAttempt, editOrder]);

  const reviseExisting = useMemo(
    () => items.find((item) => String(item.id) === revisionOf),
    [items, revisionOf],
  );
  const retainedCount = Number(reviseExisting?.editor_data?.attachment_count || 0);
  const fileSize = files.reduce((sum, file) => sum + file.size, 0);

  const clearPreview = () => {
    requestVersion.current += 1;
    setPreviewReady(false);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = '';
    setPreviewUrl('');
  };

  const setField = (key, value) => {
    clearPreview();
    setFields((current) => ({ ...current, [key]: value }));
  };

  const selectFiles = (nextFiles) => {
    clearPreview();
    setFiles(nextFiles);
  };

  const buildData = () => ({
    ...fields,
    title: fields.title.trim(),
    change_number: fields.change_number.trim(),
    price_adjustment: fields.price_adjustment.trim() || null,
    revision_of: revisionOf || null,
    ...(revisionOf ? { expected_hash: expectedHash } : {}),
    context: context || {},
    attachment_count: retainedCount + files.length,
  });

  const validate = () => {
    if (!context || !Object.keys(context).length)
      return contextError || 'Job and contract context is still loading.';
    if (!fields.title.trim()) return 'Enter a change order title.';
    if (!fields.change_number.trim()) return 'Enter a change number.';
    if (!fields.scope_changes.trim()) return 'Describe the scope changes.';
    if (fields.scope_changes.length > 16000)
      return 'Scope changes must be 16,000 characters or fewer.';
    if (fields.schedule_changes.length > 8000)
      return 'Schedule changes must be 8,000 characters or fewer.';
    if (fields.payment_terms.length > 8000)
      return 'Payment terms must be 8,000 characters or fewer.';
    if (
      fields.price_adjustment.trim() &&
      !/^[+-]?\d{1,9}(?:\.\d{1,2})?$/.test(fields.price_adjustment.trim())
    ) {
      return 'Use up to 9 integral digits and 2 decimal places for the signed price adjustment.';
    }
    if (revisionOf && !expectedHash)
      return 'The source revision hash is missing. Refresh the records and try again.';
    if (
      items.some(
        (item) =>
          item.change_number?.trim().toLowerCase() === fields.change_number.trim().toLowerCase(),
      )
    ) {
      return 'That change number is already in use. Enter a unique change number for this revision.';
    }
    if (
      files.some(
        (file) =>
          (file.type && file.type !== 'application/pdf') ||
          !file.name.toLowerCase().endsWith('.pdf'),
      )
    ) {
      return 'Only PDF attachments are accepted.';
    }
    if (fileSize > 10 * 1024 * 1024) return 'Combined attachments must be 10 MB or smaller.';
    return '';
  };

  const createFormData = () => {
    const formData = new FormData();
    formData.append('data', JSON.stringify(buildData()));
    files.forEach((file) => formData.append('attachments', file, file.name));
    return formData;
  };

  const previewDraft = async () => {
    setError('');
    setPreviewReady(false);
    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }
    const version = ++requestVersion.current;
    setPreviewing(true);
    try {
      const response = await fetch(
        `/api/jobs/${encodeURIComponent(jobId)}/change-orders/native/preview`,
        {
          method: 'POST',
          headers: authHeaders,
          body: createFormData(),
        },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Unable to preview this change order.');
      }
      if (!response.headers.get('content-type')?.includes('application/pdf')) {
        throw new Error('The service did not return a PDF. Your input has been kept.');
      }
      const url = URL.createObjectURL(await response.blob());
      if (version !== requestVersion.current) {
        URL.revokeObjectURL(url);
        return;
      }
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setPreviewReady(true);
    } catch (previewFailure) {
      if (version === requestVersion.current)
        setError(errorMessage(previewFailure, 'Unable to preview this change order.'));
    } finally {
      setPreviewing(false);
    }
  };

  const closeEditor = () => {
    setOpen(false);
    setFields(emptyFields);
    setFiles([]);
    setRevisionOf('');
    setExpectedHash('');
    setError('');
    setPreviewReady(false);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = '';
    setPreviewUrl('');
  };

  const saveDraft = async () => {
    setError('');
    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }
    if (!previewReady) {
      setError(
        'Preview the current draft before saving. This checks the complete PDF against the 40-page limit.',
      );
      return;
    }
    const version = ++requestVersion.current;
    setSaving(true);
    try {
      const response = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/change-orders/native`, {
        method: 'POST',
        headers: authHeaders,
        body: createFormData(),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to save the native draft.');
      if (version === requestVersion.current) {
        closeEditor();
        await onSaved?.(payload.change_order);
      }
    } catch (saveError) {
      if (version === requestVersion.current)
        setError(errorMessage(saveError, 'Unable to save the native draft.'));
    } finally {
      setSaving(false);
    }
  };

  const startNew = () => {
    setFields(emptyFields);
    setFiles([]);
    setRevisionOf('');
    setExpectedHash('');
    setContext(null);
    setContextError('');
    setError('');
    setPreviewReady(false);
    setOpen(true);
  };

  return {
    open,
    context,
    contextError,
    fields,
    files,
    revisionOf,
    previewUrl,
    previewReady,
    saving,
    previewing,
    error,
    fileSize,
    retainedCount,
    setContextAttempt,
    setField,
    selectFiles,
    previewDraft,
    saveDraft,
    closeEditor,
    startNew,
  };
}
